import deepmerge from "deepmerge";
import { debounce } from "lodash";
import uuid from "uuid-random";
import { reveal, minimizeAll, layoutToWorkspace, workspaceToLayout, getImportStatus, objectFlatMap, asyncForEach, toGoLang, safeInteropRegister, orderedLayouts, toGoLangInvoke, FINSEMBLECONFIG, FSBLPromise } from "../common/windowFunctions";
import { logStatus } from "../utils";
import { PromiseWrapper } from "../utils/pw";
export function camelCase(...args) {
    let str = "";
    args.forEach((arg) => {
        str = `${str} ${arg}`;
    });
    return str
        .replace(/\s(.)/g, ($1) => $1.toUpperCase())
        .replace(/\s/g, "")
        .replace(/^(.)/, ($1) => $1.toLowerCase());
}
export function getNextWorkspaceName(name, workspaceNames) {
    const endsInDigit = /\(\d+\)$/;
    const nameMinusDigit = name.replace(endsInDigit, "").trim();
    const digits = [...workspaceNames, name]
        .filter((x) => camelCase(x).includes(camelCase(nameMinusDigit)))
        .map((n) => {
        const digit = n.match(endsInDigit);
        return digit === null
            ? 0
            :
                Number(digit[0].slice(1, -1));
    });
    const nextDigitInSeries = Math.max(...digits) + 1;
    return `${nameMinusDigit} (${nextDigitInSeries})`;
}
const ensureUniqueName = async (glue, desiredName, loggerClient) => {
    const { err, data: existingLayouts } = await toGoLang(() => glue.layouts.getAll("Global"));
    if (err || !existingLayouts) {
        loggerClient.error(err);
        return false;
    }
    const existingNames = existingLayouts.map((layout) => layout.name);
    return existingNames.includes(desiredName) ? getNextWorkspaceName(desiredName, existingNames) : desiredName;
};
export class WorkspaceClientImpl {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.debouncedUpdateLocalMembers = null;
        this.handlers = [];
        this.activeWorkspace = {
            version: "1.0.0",
            name: "Default workspace",
            type: "workspace",
            groups: {},
            windows: []
        };
        this.workspaces = [];
        const pw = asyncConstructors.addConstructor();
        this.loggerClient.verbose("WorkspaceClient Constructor");
        gluePromise.then(async (g) => {
            this.glue = g;
            this.initialize().finally(() => {
                this.loggerClient.verbose("WorkspaceClient initialized");
                asyncConstructors.finishConstructor(pw);
            });
        });
        this.minimizeAll = this.minimizeAll.bind(this);
        this.bringWindowsToFront = this.bringWindowsToFront.bind(this);
        this.autoArrange = this.autoArrange.bind(this);
    }
    async triggerWorkspaceEvent(event, workspaceName) {
        const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.triggerEventHandlers", {
            event,
            workspaceName
        }));
        if (err) {
            this.loggerClient.error("triggerWorkspaceEvent", event, workspaceName, err);
        }
        return { err, data };
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "WorkspaceClient",
            method, type, data
        });
    }
    async initialize() {
        const a = this.registerGlueEventHandlers();
        const b = this.updateLocalMembers();
        await FSBLPromise().allSettled([a, b]);
    }
    async updateGlobalLayouts() {
        const { err, data: layouts = [] } = await toGoLang(() => this.glue.layouts.getAll("Global") ?? []);
        if (err) {
            this.loggerClient.error("WorkspaceClient.updateLocalMembers.getAll()", err);
        }
        this.workspaces = layouts.map((layout) => layout.name);
    }
    async updateCurrentLayout() {
        const { err: err2, data: currentLayout } = await toGoLang(() => this.glue.layouts.getCurrentLayout());
        if (err2) {
            if (err2.toString().includes("Invocation timeout (30000 ms)")) {
                return;
            }
            this.loggerClient.error("WorkspaceClient.updateLocalMembers.getCurrentLayout()", err2);
        }
        if (currentLayout) {
            this.activeWorkspace = layoutToWorkspace(currentLayout);
        }
    }
    async updateLocalMembers() {
        if (!this.debouncedUpdateLocalMembers) {
            this.debouncedUpdateLocalMembers = debounce(async () => {
                this.loggerClient.verbose("WorkspaceClient updateLocalMembers");
                const a = this.updateCurrentLayout();
                const b = this.updateGlobalLayouts();
                await FSBLPromise().allSettled([a, b]);
            }, 1500);
        }
        return await this.debouncedUpdateLocalMembers();
    }
    async registerGlueEventHandlers() {
        const a = this.glue.layouts.onAdded(() => { this.updateLocalMembers(); });
        const b = this.glue.layouts.onChanged(() => { this.updateLocalMembers(); });
        const c = this.glue.layouts.onRemoved(() => { this.updateLocalMembers(); });
        const d = this.glue.layouts.onRenamed(() => { this.updateLocalMembers(); });
        const e = this.glue.layouts.onRestored(() => { this.updateLocalMembers(); });
        await FSBLPromise().allSettled([a, b, c, d, e]);
    }
    async removeEventListener(event, handler) {
        this.logStatus("removeEventListener", "start", { event });
        await this.asyncConstructors.ready();
        const index = this.handlers.findIndex((item) => item.event === event && item.handler === handler);
        if (index === -1) {
            return;
        }
        const { uuid } = this.handlers[index];
        this.handlers.splice(index, 1);
        const { err } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.removeEventListener", {
            event,
            id: uuid
        }));
        if (err) {
            this.logStatus("removeEventListener", "error", err);
        }
        this.glue.interop.unregister(uuid);
        this.logStatus("removeEventListener", "return");
    }
    async addEventListener(event, handler) {
        this.logStatus("addEventListener", "start", event);
        await this.asyncConstructors.ready();
        const id = uuid();
        await this.glue.interop.register(id, safeInteropRegister(async (data) => {
            let waitRequested = false;
            const doneTriggered = new PromiseWrapper();
            const event = {
                data,
                wait: () => {
                    waitRequested = true;
                },
                done: (newData) => {
                    doneTriggered.resolve({ action: "done", newData });
                },
                cancel: () => {
                    doneTriggered.resolve({ action: "cancel" });
                }
            };
            handler(event);
            if (waitRequested) {
                return await doneTriggered.promise;
            }
        }));
        this.handlers.push({
            event,
            handler,
            uuid: id
        });
        const { err } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.addEventListener", {
            event,
            uuid: id
        }));
        if (err) {
            this.logStatus("addEventListener", "error", err);
        }
        this.logStatus("addEventListener", "return");
    }
    async autoArrange(params, cb) {
        this.logStatus("autoArrange", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.glue.windows.autoArrange());
        this.logStatus("autoArrange", "return", { err });
        cb?.(err);
        return {
            err
        };
    }
    async minimizeAll(params, cb) {
        this.logStatus("minimizeAll", "start", params);
        await this.asyncConstructors.ready();
        const response = await minimizeAll(this.glue, params, cb);
        this.logStatus("minimizeAll", "return", response);
        return response;
    }
    async bringWindowsToFront(params, cb) {
        this.logStatus("bringWindowsToFront", "start", params);
        await this.asyncConstructors.ready();
        const response = await reveal(this.glue, params, cb);
        this.logStatus("bringWindowsToFront", "return", response);
        return response;
    }
    async getActiveWorkspacePreUserStage(cb) {
        const { err, data: prefs } = await toGoLang(() => this.glue.prefs.get("IO.Global.FinsemblePreferences"));
        if (err) {
            this.logStatus("getActiveWorkspace", "error", err);
        }
        const { err: err1, data: lastWorkspacePref } = await toGoLang(() => this.glue.prefs.get("IO.Global.FinsembleLastWorkspace"));
        if (err1) {
            this.logStatus("getActiveWorkspace", "error", err1);
        }
        const { err: err2, data: manifest } = await toGoLang(() => this.glue.contexts.get(FINSEMBLECONFIG));
        if (err2) {
            this.logStatus("getActiveWorkspace", "error", err2);
        }
        const activeLayoutName = prefs?.data?.["finsemble.initialWorkspace"] ?? manifest?.finsemble?.initialWorkspace ?? lastWorkspacePref?.data?.name ?? "Default Workspace";
        const { err: err3, data: currentLayout } = await toGoLang(() => this.glue.layouts.get(activeLayoutName, "Global"));
        if (err3) {
            this.logStatus("getActiveWorkspace", "error", err3);
        }
        if (!currentLayout) {
            return null;
        }
        const data = layoutToWorkspace(currentLayout);
        cb?.(null, { data });
        return { data };
    }
    async getActiveWorkspace(cb, withoutChanges) {
        this.logStatus("getActiveWorkspace", "start");
        await this.asyncConstructors.ready();
        try {
            const currentLayout = await this.glue.layouts.getCurrentLayout();
            if (!currentLayout) {
                const workspace = await this.getActiveWorkspacePreUserStage(cb);
                if (workspace) {
                    return workspace;
                }
            }
            if (currentLayout && withoutChanges) {
                const data = layoutToWorkspace(currentLayout);
                this.logStatus("getActiveWorkspace", "return", { data });
                cb?.(null, data);
                return { data };
            }
            const blob = await this.getBlobForCurrentLayout(currentLayout);
            const workspace = blob ? layoutToWorkspace(blob) : {
                "version": "1.0.0",
                "name": "",
                "type": "workspace",
                "groups": {},
                "windows": [],
                "componentStates": {}
            };
            if (workspace.name.includes("#internal-tmp")) {
                workspace.name = "";
            }
            const finalizedWorkspace = {
                ...workspace,
                name: currentLayout?.name ?? workspace.name
            };
            this.logStatus("getActiveWorkspace", "return", { data: finalizedWorkspace });
            cb?.(null, finalizedWorkspace);
            return { data: finalizedWorkspace };
        }
        catch (e) {
            const err = e.toString();
            this.logStatus("getActiveWorkspace", "error", err);
            cb?.(err);
            throw e;
        }
    }
    async getWorkspaces(cb) {
        this.logStatus("getWorkspaces", "start");
        await this.asyncConstructors.ready();
        const layouts = await this.glue.layouts.getAll("Global");
        const ordered = await orderedLayouts(layouts);
        const result = ordered.map((layout) => layoutToWorkspace(layout));
        this.logStatus("getWorkspaces", "return", result);
        cb?.(null, result);
        return result;
    }
    getWorkspaceActions(cb) {
        this.logStatus("getWorkspaceActions", "start");
        const actions = {
            NEW_WORKSPACE: "Finsemble.Workspace.NewWorkspace",
            SAVE: "Finsemble.Workspace.Save",
            RENAME: "Finsemble.Workspace.Rename",
            SAVE_AS: "Finsemble.Workspace.SaveAs",
            SWITCH_TO: "Finsemble.Workspace.SwitchTo",
            IMPORT: "Finsemble.Workspace.Import",
            EXPORT: "Finsemble.Workspace.Export",
            REMOVE: "Finsemble.Workspace.Remove",
            SAVE_GLOBAL_DATA: "Finsemble.Workspace.SaveGlobalData",
            SAVE_VIEW_DATA: "Finsemble.Workspace.SaveViewData",
            GET_GLOBAL_DATA: "Finsemble.Workspace.GetGlobalData",
            GET_VIEW_DATA: "Finsemble.Workspace.GetViewData",
            GET_WORKSPACES: "Finsemble.Workspace.GetWorkspaces",
            GET_WORKSPACE_NAMES: "Finsemble.Workspace.GetWorkspaceNames",
            SET_WORKSPACE_ORDER: "Finsemble.Workspace.SetWorkspaceOrder",
            GET_ACTIVE_WORKSPACE: "Finsemble.Workspace.GetActiveWorkspace",
            GET_ACTIVE_WORKSPACE_DIRTY: "Finsemble.Workspace.GetActiveWorkspaceDirty",
            GET_TEMPLATES: "Finsemble.Workspace.GetTemplates",
            IMPORT_TEMPLATE: "Finsemble.Workspace.ImportTemplate",
            EXPORT_TEMPLATE: "Finsemble.Workspace.ExportTemplate",
            REMOVE_TEMPLATE: "Finsemble.Workspace.RemoveTemplate",
            SET_WINDOW_STATE: "Finsemble.Workspace.SetWindowData",
            GET_WINDOW_STATE: "Finsemble.Workspace.GetWindowData",
            ADD_WINDOW: "WorkspaceService.addWindow",
            REMOVE_WINDOW: "WorkspaceService.removeWindow",
        };
        this.logStatus("getWorkspaceActions", "return", actions);
        cb?.(null, actions);
        return actions;
    }
    async getWorkspaceNames(cb) {
        this.logStatus("getWorkspaceNames", "start");
        await this.asyncConstructors.ready();
        const { err, data: layouts } = await toGoLang(() => this.glue.layouts.getAll("Global"));
        const error = err ? `WorkspaceClient.getWorkspaceNames() ${err}` : null;
        if (error) {
            this.loggerClient.error(error);
        }
        const workspaces = layouts?.map((layout) => layout.name) ?? [];
        this.logStatus("getWorkspaceNames", "return", workspaces);
        cb?.(error, workspaces);
        return workspaces;
    }
    async setWorkspaceOrder(params, cb) {
        await this.asyncConstructors.ready();
        const { workspaces } = params;
        const { err, data: layouts } = await toGoLang(() => this.glue.layouts.getAll("Global"));
        if (err || !layouts) {
            this.loggerClient.error("WorkspaceClient.setWorkspaceOrder()", err);
            cb?.(err);
            return;
        }
        const reorderedLayouts = layouts.map((layout) => {
            const position = workspaces.findIndex((w) => w.name.name === layout.name);
            layout.metadata = deepmerge(layout.metadata ?? {}, {
                "iop#position": position
            });
            return layout;
        });
        await asyncForEach(reorderedLayouts, async (layout) => {
            await this.glue.layouts.save({
                ...layout,
                setAsCurrent: false
            });
        });
        cb?.(null);
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.order", { workspaces: workspaces.map((ws) => ws.name.name) }));
    }
    setWorkspaces(params, cb) {
        return this.setWorkspaceOrder(params, cb);
    }
    async remove(params, cb) {
        this.logStatus("remove", "start", params);
        await this.asyncConstructors.ready();
        const { workspace, name } = params;
        const workspaceName = name ?? workspace?.name;
        const { err } = workspaceName ? await toGoLang(() => this.glue.layouts.remove("Global", workspaceName)) : { err: null };
        this.logStatus("remove", "return");
        cb?.(err);
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.remove", { workspaces: [workspaceName] }));
    }
    async rename(params, cb) {
        this.logStatus("rename", "start", params);
        await this.asyncConstructors.ready();
        const { oldName, newName } = params;
        const realNewName = await ensureUniqueName(this.glue, newName, this.loggerClient);
        if (!realNewName) {
            this.logStatus("rename", "warn", `Can't get unique name for ${newName}`);
            return;
        }
        const { err, data: oldLayout } = await toGoLang(() => this.glue.layouts.get(oldName, "Global"));
        if (err) {
            this.loggerClient.error("WorkspaceClient.rename.get()", err);
        }
        if (oldLayout) {
            const { err } = await toGoLang(() => this.glue.layouts.rename(oldLayout, realNewName));
            if (err) {
                this.loggerClient.error("WorkspaceClient.rename.rename()", err);
            }
        }
        this.logStatus("rename", "return");
        await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.rename", { oldName, newName: realNewName }));
        cb?.(err);
    }
    async saveAs(params, cb) {
        this.logStatus("saveAs", "start", params);
        await this.asyncConstructors.ready();
        if (!params.force) {
            const { err, data: layouts } = await toGoLang(() => this.glue.layouts.getAll("Global"));
            const error = `WorkspaceClient:saveAs ${err}`;
            if (err || !layouts) {
                this.loggerClient.error(error);
                cb?.(error);
                return;
            }
            if (layouts.some((layout) => layout.name === params.name)) {
                const err = `Layout ${params.name} already exists. Set force: true to overwrite`;
                cb?.(err);
                return;
            }
        }
        const { data: results } = await this.triggerWorkspaceEvent("save-requested", params.name);
        if (results?.some((result) => result?.action === "cancel")) {
            cb?.(null);
            return;
        }
        const { data: currentLayout } = await toGoLang(() => this.glue.layouts.getCurrentLayout());
        const { err } = await toGoLang(() => this.glue.layouts.save({
            name: params.name,
            type: "Global",
            setAsCurrent: true,
            metadata: currentLayout?.metadata
        }));
        if (err) {
            await this.triggerWorkspaceEvent("save-failed", params.name);
            this.loggerClient.error("WorkspaceClient.saveAs.save", err);
        }
        else {
            await this.triggerWorkspaceEvent("save-complete", params.name);
        }
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.save", { workspaces: [params.name] }));
        this.logStatus("saveAs", "return", params);
        cb?.(err);
    }
    async save(cb) {
        this.logStatus("save", "start");
        await this.asyncConstructors.ready();
        const { err, data: currentLayout } = await toGoLang(() => this.glue.layouts.getCurrentLayout());
        if (err) {
            this.logStatus("save", "error", `getCurrentLayout: ${err}`);
        }
        if (!currentLayout) {
            const error = `No current layout. Use saveAs ${err}`;
            this.logStatus("save", "error", error);
            cb?.(error);
            return { err: error };
        }
        const { data: results } = await this.triggerWorkspaceEvent("save-requested", currentLayout.name);
        if (results?.some((result) => result?.action === "cancel")) {
            cb?.(null);
            return {};
        }
        const { err: err2 } = await toGoLang(() => this.glue.layouts.save({
            name: currentLayout.name,
            type: "Global",
            setAsCurrent: false,
            metadata: currentLayout?.metadata
        }));
        if (err2) {
            await this.triggerWorkspaceEvent("save-failed", currentLayout.name);
            this.logStatus("save", "error", `save: ${err2}`);
        }
        else {
            await this.triggerWorkspaceEvent("save-complete", currentLayout.name);
        }
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.save", { workspaces: [currentLayout.name] }));
        this.logStatus("save", "return", { err: err2 });
        cb?.(err2);
        return { err: err2 };
    }
    async switchTo(params, cb) {
        this.logStatus("switchTo", "start", params);
        await this.asyncConstructors.ready();
        const { data: currentLayout } = await toGoLang(() => this.glue.layouts.getCurrentLayout());
        currentLayout?.name && await this.triggerWorkspaceEvent("close-requested", currentLayout.name);
        const { data: results } = await this.triggerWorkspaceEvent("load-requested", params.name);
        if (results?.some((result) => result?.action === "cancel")) {
            cb?.(null);
            return { data: undefined };
        }
        if (results?.length) {
            const layouts = [];
            results.forEach((result) => {
                if (result.newData) {
                    layouts.push(result.newData);
                }
            });
            const { err } = await toGoLang(() => this.glue.layouts.import(layouts, "merge"));
            if (err) {
                this.logStatus("switchTo", "error", { err, message: "Error importing layout from workspace event" });
            }
        }
        const { err } = await toGoLang(() => this.glue.layouts.restore({ name: params.name }));
        if (err) {
            currentLayout?.name && this.triggerWorkspaceEvent("close-failed", currentLayout.name);
            this.triggerWorkspaceEvent("load-failed", params.name);
        }
        else {
            currentLayout?.name && this.triggerWorkspaceEvent("close-complete", currentLayout.name);
            this.triggerWorkspaceEvent("load-complete", params.name);
            const { data: restoredLayout } = await toGoLang(() => this.glue.layouts.getCurrentLayout());
            await toGoLang(() => this.glue.layouts.save({ name: params.name, type: "Global", metadata: restoredLayout?.metadata }));
            await toGoLang(() => this.glue.prefs.setFor("IO.Global.FinsembleLastWorkspace", { name: params.name }));
        }
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.switchTo", {}));
        this.logStatus("switchTo", "return", { err, data: undefined });
        cb?.(err);
        return { data: undefined };
    }
    async getBlobForCurrentLayout(currentLayout) {
        const name = `#internal-tmp-${uuid()}`;
        const { err } = await toGoLang(() => this.glue.layouts.save({ name, type: "Global", setAsCurrent: false, metadata: currentLayout?.metadata }));
        if (err) {
            this.logStatus("getBlobForCurrentLayout layouts.save()", "error", err.toString());
        }
        const { err: err2, data: layouts } = await toGoLang(() => this.glue.layouts.export("Global"));
        if (err2) {
            this.logStatus("getBlobForCurrentLayout layouts.export()", "error", err2.toString());
        }
        const tmp = layouts?.find((l) => l.name === name);
        const { err: err3 } = await toGoLang(() => this.glue.layouts.remove("Global", name));
        if (err3) {
            this.logStatus("getBlobForCurrentLayout layouts.remove()", "error", err3.toString());
        }
        return tmp;
    }
    async isWorkspaceDirty(cb) {
        this.logStatus("isWorkspaceDirty", "start");
        const filterToMinimal = (components) => components.map((component) => {
            return {
                application: component.application,
                state: {
                    bounds: {
                        left: component.state?.bounds?.left,
                        top: component.state?.bounds?.top,
                        width: component.state?.bounds?.width,
                        height: component.state?.bounds?.height,
                    },
                    channelId: component?.state?.channelId,
                    channelRestrictions: component?.state?.channelRestrictions,
                    tabIndex: component?.state?.tabIndex,
                    windowState: component?.state?.windowState
                }
            };
        });
        await this.asyncConstructors.ready();
        try {
            const currentLayout = await this.glue.layouts.getCurrentLayout();
            const blob = await this.getBlobForCurrentLayout(currentLayout);
            const layouts = await this.glue.layouts.export("Global");
            const current = layouts.find((l) => l.name === currentLayout?.name);
            const minimalOld = filterToMinimal(current?.components ?? []);
            const minimalNew = filterToMinimal(blob?.components ?? []);
            this.logStatus("isWorkspaceDirty", "debug", { minimalOld, minimalNew });
            const isDirty = JSON.stringify(minimalOld) !== JSON.stringify(minimalNew);
            this.logStatus("isWorkspaceDirty", "return", isDirty);
            cb?.(null, isDirty);
            return isDirty;
        }
        catch (e) {
            this.logStatus("isWorkspaceDirty", "error", e);
        }
        this.logStatus("isWorkspaceDirty", "return", false);
        cb?.(null, false);
        return false;
    }
    async createWorkspace(workspaceName, params, cb) {
        this.logStatus("createWorkspace", "start", { workspaceName, params });
        await this.asyncConstructors.ready();
        const name = await ensureUniqueName(this.glue, workspaceName, this.loggerClient);
        if (!name) {
            const result = {
                workspaceName: "An error occurred creating workspace"
            };
            this.logStatus("createWorkspace", "error", result);
            cb?.("Cannot create unique name", result);
            return result;
        }
        const newLayout = {
            type: "Global",
            name,
            components: []
        };
        const { err, data: layoutResult } = await toGoLang(() => this.glue.layouts.import([newLayout], "merge"));
        if (err || !layoutResult) {
            const error = `WorkspaceClient.createWorkspace.import ${err}`;
            const result = {
                workspaceName: error
            };
            this.logStatus("createWorkspace", "error", error);
            cb?.(error, result);
            return result;
        }
        const error = layoutResult.status !== "Success" ? layoutResult.status : null;
        const result = {
            workspaceName: name
        };
        if (params?.switchAfterCreation !== false) {
            await this.switchTo({ name });
        }
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.save", { workspaces: [name] }));
        this.logStatus("createWorkspace", "return", { error, result });
        cb?.(error, result);
        return result;
    }
    async import(params, cb) {
        this.logStatus("import", "start", params);
        await this.asyncConstructors.ready();
        const { workspaceJSONDefinition, force } = params;
        const { err, data: existingLayouts } = await toGoLang(() => this.glue.layouts.getAll("Global"));
        if (err || !existingLayouts) {
            const error = `WorkspaceClient.import:getAll() ${err}`;
            console.error(error);
            this.logStatus("import", "error", error);
            cb?.(error, {});
            return {};
        }
        const maybeAdjustedWorkspaces = objectFlatMap(workspaceJSONDefinition, (key, value) => {
            if (force) {
                return [[key, value]];
            }
            const existingNames = existingLayouts.map((layout) => layout.name);
            return [[existingNames.includes(key) ? getNextWorkspaceName(key, existingNames) : key, value]];
        });
        const layouts = Object.entries(maybeAdjustedWorkspaces).map(([workspaceName, finsembleWorkspace], index) => workspaceToLayout(workspaceName, finsembleWorkspace, index));
        const { err: err2, data: glueImportResult } = await toGoLang(() => this.glue.layouts.import(layouts, "merge"));
        if (err2 || !glueImportResult) {
            const error = `WorkspaceClient.import:getAll() ${err2}`;
            this.logStatus("import", "error", error);
            cb?.(error, {});
            return {};
        }
        const results = Object.fromEntries(Object.keys(maybeAdjustedWorkspaces).map((name) => [name, getImportStatus(name, glueImportResult)]));
        toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.WorkspaceClient.storageAdapter.save", { workspaces: layouts.map((layout) => layout.name) }));
        this.logStatus("import", "return", results);
        cb?.(null, results);
        return results;
    }
    async importWorkspace(params, cb) {
        await this.asyncConstructors.ready();
        return this.import(params, cb);
    }
    async export(params, cb) {
        this.logStatus("export", "start", params);
        await this.asyncConstructors.ready();
        const { workspaceName } = params;
        const { err, data: layout } = await toGoLang(() => this.glue.layouts.get(workspaceName, "Global"));
        if (err) {
            this.logStatus("export", "error", err);
        }
        if (err || !layout) {
            const error = `Layout ${workspaceName} not found`;
            this.logStatus("export", "error", error);
            cb?.(error);
            throw new Error(error);
        }
        const result = {
            [workspaceName]: layoutToWorkspace(layout)
        };
        this.logStatus("export", "return", result);
        cb?.(null, result);
        return result;
    }
    async exportWorkspace(params, cb) {
        return this.export(params, cb);
    }
}
